/**
 * Campo de mídia do documento: URL colada OU arquivo enviado direto ao S3
 * (vídeo/áudio, com progresso). Para `frame`, aceita link normal ou o <iframe>
 * inteiro e grava só a URL de embed. Prévia embaixo lê a duração do arquivo.
 * Cores só por tokens — o card do formulário está em tema claro/escuro da página.
 */
import React, { useRef, useState } from 'react';
import type { DocumentMediaType } from '@troia-v3/types';
import { Input } from '@/shared/components/ui/Input';
import { Icon } from '@/shared/icons';
import { toEmbedUrl } from '@/shared/utils/mediaEmbed';
import { uploadFileWithPresign } from '@/shared/utils/presignedUpload';

export interface MediaUploadInputProps {
  mediaType: DocumentMediaType;
  value: string;
  onChange: (url: string) => void;
  /** Duração lida da prévia (segundos) — só video/audio */
  onDuration?: (seconds: number) => void;
  folder?: string;
  testId?: string;
}

const ACCEPT: Record<DocumentMediaType, string> = { video: 'video/*', audio: 'audio/*', frame: '' };

export const MediaUploadInput: React.FC<MediaUploadInputProps> = ({
  mediaType,
  value,
  onChange,
  onDuration,
  folder = 'documents',
  testId = 'media-upload',
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File): Promise<void> => {
    setError(null);
    setProgress(0);
    try {
      const url = await uploadFileWithPresign(file, folder, setProgress);
      onChange(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha no envio');
    } finally {
      setProgress(null);
    }
  };

  const handleFrameText = (text: string): void => {
    if (!text.trim()) { setError(null); onChange(''); return; }
    const embed = toEmbedUrl(text);
    if (!embed) { setError('Cole um link https ou o código do iframe (YouTube, Vimeo, Loom...)'); onChange(text); return; }
    setError(null);
    onChange(embed);
  };

  const isFrame = mediaType === 'frame';

  return (
    <div className="space-y-2">
      <div className="flex gap-1.5">
        <div className="flex-1">
          <Input
            value={value}
            onChange={(e) => (isFrame ? handleFrameText(e.target.value) : onChange(e.target.value))}
            placeholder={isFrame ? 'https://www.youtube.com/watch?v=... ou <iframe ...>' : 'https://... ou envie o arquivo'}
            data-testid={`${testId}-url`}
          />
        </div>
        {!isFrame && (
          <button
            type="button"
            className="inline-flex items-center gap-1.5 h-[38px] px-3 rounded-[9px] border text-sm font-medium flex-shrink-0"
            style={{ backgroundColor: 'var(--theme-input-background)', borderColor: 'var(--theme-input-border)', color: 'var(--theme-card-text)' }}
            onClick={() => fileRef.current?.click()}
            disabled={progress !== null}
            data-testid={`${testId}-button`}
          >
            <Icon name="upload" className="w-4 h-4" />
            {progress === null ? 'Enviar' : `${progress}%`}
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          accept={ACCEPT[mediaType]}
          data-testid={`${testId}-file`}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            e.target.value = '';
          }}
        />
      </div>

      {progress !== null && (
        <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--theme-surface-sunken)' }} data-testid={`${testId}-progress`}>
          <div className="h-full" style={{ width: `${progress}%`, backgroundColor: 'var(--theme-primary)' }} />
        </div>
      )}

      {error && (
        <p className="text-xs" style={{ color: 'var(--theme-error-color)' }} data-testid={`${testId}-error`}>{error}</p>
      )}

      {value && !error && (
        <div className="rounded-[9px] overflow-hidden border" style={{ borderColor: 'var(--theme-card-border)', backgroundColor: 'var(--theme-media-frame-background)' }} data-testid={`${testId}-preview`}>
          {mediaType === 'video' && (
            <video src={value} controls preload="metadata" className="w-full aspect-video" onLoadedMetadata={(e) => { if (Number.isFinite(e.currentTarget.duration)) onDuration?.(e.currentTarget.duration); }} />
          )}
          {mediaType === 'audio' && (
            <audio src={value} controls preload="metadata" className="w-full p-2" onLoadedMetadata={(e) => { if (Number.isFinite(e.currentTarget.duration)) onDuration?.(e.currentTarget.duration); }} />
          )}
          {mediaType === 'frame' && (
            <iframe
              src={value}
              title="Prévia da mídia incorporada"
              className="w-full aspect-video"
              sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
              allow="autoplay; fullscreen; picture-in-picture"
              referrerPolicy="strict-origin-when-cross-origin"
              loading="lazy"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default MediaUploadInput;
